import { apiClient } from "./client"

import type { ReferenceDocument, ReferenceKind, ReferenceListResponse } from "@/types"

export interface CreateReferencePayload {
  kind: ReferenceKind
  title?: string
  subject_id?: string | null
  file?: File
  text?: string
  url?: string
  page_from?: number | null
  page_to?: number | null
}

export interface UpdateReferencePayload {
  title?: string
  subject_id?: string | null
}

export const referencesApi = {
  list: () => apiClient.get<ReferenceListResponse>("/references/").then((r) => r.data),
  get: (id: string) => apiClient.get<ReferenceDocument>(`/references/${id}`).then((r) => r.data),
  create: (
    payload: CreateReferencePayload,
    onProgress?: (loaded: number, total: number) => void,
  ) => {
    const form = new FormData()
    form.append("kind", payload.kind)
    // The consent line is a server contract (PRD-64 FR-4): the form cannot
    // submit without the checkbox, so the value is always true here.
    form.append("consent", "true")
    if (payload.title) form.append("title", payload.title)
    if (payload.subject_id) form.append("subject_id", payload.subject_id)
    if (payload.text) form.append("text", payload.text)
    if (payload.url) form.append("url", payload.url)
    if (payload.page_from) form.append("page_from", String(payload.page_from))
    if (payload.page_to) form.append("page_to", String(payload.page_to))
    if (payload.file) form.append("file", payload.file, payload.file.name)
    const total = payload.file?.size ?? payload.text?.length ?? 0
    return apiClient
      .post<ReferenceDocument>("/references/", form, {
        headers: { "Content-Type": "multipart/form-data" },
        // Same reason as the student portal: a 20 MB chapter over a slow
        // link is minutes, and an abort mid-upload reads as "algo salió mal".
        timeout: 0,
        onUploadProgress: (e) => onProgress?.(e.loaded, e.total ?? total),
      })
      .then((r) => r.data)
  },
  update: (id: string, payload: UpdateReferencePayload) =>
    apiClient.patch<ReferenceDocument>(`/references/${id}`, payload).then((r) => r.data),
  retry: (id: string) =>
    apiClient.post<ReferenceDocument>(`/references/${id}/retry`).then((r) => r.data),
  remove: (id: string) =>
    apiClient.delete<{ success: boolean }>(`/references/${id}`).then((r) => r.data),
}
